import type { StepDescriptor, StepId } from "./route-table.ts";
import type { ResumeTarget, SectionStateEntry } from "./types.ts";

/**
 * Pure resume function: (section-state.ts's output, the step table) ->
 * {targetStep, targetSectionId?}. No import of the router, no import of
 * any component, no I/O (AC-2, AC-8). No section id and no step path
 * literal — every id it returns is one handed to it in `sectionStates`
 * or `stepTable`, both supplied by the caller (route-table.ts is never
 * imported here).
 *
 * Returns the step hosting the FIRST unconfirmed, applicable section in
 * step-and-section order (AC-2) — computed once by section-state.ts and
 * simply looked up here, per ADR-20260918-dd9e's single shared
 * computation. When section-state.ts reports no section as "open" at all
 * (every applicable section confirmed, or an entire step's sections
 * inapplicable — AC-6's change-check "no" case), the target is the
 * FIRST, in step order, among the hostless steps (declaration, received)
 * that is NOT YET reported settled by the caller-supplied
 * `isHostlessStepSettled` — the next single-purpose step once every
 * section-hosting step is settled. If every hostless step is already
 * reported settled (OBJ-130/FIND-190: an in-session declaration
 * submission settles "declaration" while this function, unfixed, kept
 * returning "declaration" regardless — the stepper read every stage
 * Completed while the primary action navigated back to the now-blank,
 * already-confirmed declaration page), the target is the LAST hostless
 * step (received) rather than looping back onto a settled one. Callers
 * with no real settlement signal must pass `() => false` (the default),
 * which reproduces the pre-fix "always the first hostless step" result
 * exactly — this is additive, not a behaviour change for any caller that
 * does not supply the new argument.
 */
export function resolveResumeTarget(
  sectionStates: readonly SectionStateEntry[],
  stepTable: readonly StepDescriptor[],
  isHostlessStepSettled: (stepId: StepId) => boolean = () => false,
): ResumeTarget {
  const openEntries = sectionStates
    .filter((entry) => entry.state === "open")
    .sort((a, b) => a.order - b.order);
  const openEntry = openEntries[0];

  if (openEntry !== undefined) {
    return { targetStep: openEntry.hostStepId, targetSectionId: openEntry.sectionId };
  }

  const hostedStepIds = new Set(sectionStates.map((entry) => entry.hostStepId));
  const hostlessStepsInOrder = [...stepTable]
    .filter((step) => !hostedStepIds.has(step.id))
    .sort((a, b) => a.order - b.order);

  if (hostlessStepsInOrder.length === 0) {
    throw new Error(
      "resolveResumeTarget: no section is open and no step exists beyond the section-hosting steps",
    );
  }

  const nextUnsettledStep = hostlessStepsInOrder.find((step) => !isHostlessStepSettled(step.id));
  if (nextUnsettledStep !== undefined) {
    return { targetStep: nextUnsettledStep.id };
  }

  // Every hostless step is reported settled: land on the LAST one
  // (received) rather than looping back onto an earlier, now-blank,
  // already-settled one (OBJ-130/FIND-190). `hostlessStepsInOrder` is
  // non-empty here (checked above), so this index is always in range.
  const lastHostlessStep = hostlessStepsInOrder.at(-1);
  if (lastHostlessStep === undefined) {
    throw new Error("resolveResumeTarget: unreachable -- hostlessStepsInOrder is non-empty");
  }

  return { targetStep: lastHostlessStep.id };
}

/**
 * The shared back-navigation resolver (TG-18, ADR-20261001-rz8p). Every
 * route's "Back" control (ChangeCheckRoute, DeclarationRoute) resolves its
 * target from here rather than hardcoding one, so there is exactly one
 * definition of "what does back mean" across the whole feature — the same
 * single-shared-computation discipline this file's own forward resolver
 * (`resolveResumeTarget`) and section-state.ts already establish.
 *
 * Pure, and — like `resolveResumeTarget` above — contains no step id and
 * no section id literal of its own: every id it returns is one handed to
 * it in `sectionStates` or `stepTable`.
 *
 * Algorithm: walk the steps declared before `stepId` (in step order),
 * nearest first. For each candidate:
 *   - If `sectionStates` holds no entry at all for that step (a
 *     single-purpose step — declaration, received), it is always on the
 *     customer's path once reached at all: back lands there directly,
 *     with no section to open.
 *   - If it holds at least one entry that is NOT "inapplicable", back
 *     lands on that step, opened to the LAST such entry (in section
 *     order) — "the last exposures section" when `stepId` is declaration
 *     and change-check was answered "yes".
 *   - If every entry for that step is "inapplicable" (the whole step was
 *     skipped — e.g. exposures when change-check is "no"), that step was
 *     never on the customer's path: keep searching further back. This is
 *     what turns declaration's back target into change-check itself when
 *     the exposures step in between was skipped entirely (the real
 *     prototype's own `fastPath` branch), with no "change-check" or
 *     "exposures" literal appearing in this file to express it.
 *
 * Returns `undefined` when `stepId` is the first declared step (or every
 * step before it is itself absent from `stepTable`, which cannot happen
 * for a well-formed table) — there is nothing to go back to, and a
 * caller renders no back control at all in that case.
 */
export function resolveBackNavigationTarget(
  stepId: StepId,
  sectionStates: readonly SectionStateEntry[],
  stepTable: readonly StepDescriptor[],
): ResumeTarget | undefined {
  const requestedStep = stepTable.find((step) => step.id === stepId);
  if (requestedStep === undefined) {
    throw new Error(`resolveBackNavigationTarget: step "${stepId}" is not present in stepTable`);
  }

  const candidateSteps = [...stepTable]
    .filter((step) => step.order < requestedStep.order)
    .sort((a, b) => b.order - a.order);

  for (const candidate of candidateSteps) {
    const hostedEntries = sectionStates
      .filter((entry) => entry.hostStepId === candidate.id)
      .sort((a, b) => b.order - a.order);

    if (hostedEntries.length === 0) {
      return { targetStep: candidate.id };
    }

    const lastReachableEntry = hostedEntries.find((entry) => entry.state !== "inapplicable");
    if (lastReachableEntry !== undefined) {
      return { targetStep: candidate.id, targetSectionId: lastReachableEntry.sectionId };
    }
    // Every section this step hosts is inapplicable — this step is not on
    // the customer's current path at all. Keep searching further back.
  }

  return undefined;
}
